import {
    ReactElement,
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    UIAvatar,
    UIEmptyState,
    UIErrorState,
    UIFilterGroup,
    UIFormInput,
    UIIcon,
    UILoadingState,
    UIMenu,
    UIMenuItem,
    UIMultiFilterGroup,
    UIMultiFilterGroupItem,
    UIMultiFilterItem,
    UIPopover,
    UIPressableAreaButtonLess,
    UIPropoverRef,
    UISection,
    UISpinner,
    UITableHeaderCell,
    UITableSortDirection,
    UIText,
    UITooltip,
    useUIConfirmationModal
} from '@/ui';
import styles from './company-users-list.module.scss';
import { CompanyUser, TeamUser } from '@/api';
import { blue, charcoal, red } from '@/ui/styles';
import { useCompanyContext } from '../providers';
import { useFetchUserCounts, useFetchUsersPaginated } from '../queries';
import { useFetchTeamFilterOptions } from '@/common/queries';
import useElementSize from '@/common/hooks/use-element-size';
import { useDebouncedValue, useFilterPopupState } from '@/common/hooks';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useInView } from 'react-intersection-observer';
import {
    useAssignLicenseAction,
    useDeleteCompanyUserAction,
    useRemoveLicenseAction,
    useResendInviteAction,
    useUpdateCompanyRoleAction
} from '../actions';
import { UpdateCompanyRoleModal } from './update-company-role-modal';
import { useCompaniesTranslation } from '../hooks';
import { TeamUserRoles } from './team-user-roles';
import { UserInfoListItem } from './user-info-list-item';
import { parseSortOrder, setOrDeleteParam, uuidv4 } from '@/common/util';

type SortBy = 'name' | 'roles' | 'teams' | 'licenses';

type MembershipStatus = 'all' | 'joined' | 'invited';

// Maps the table's sort columns to the API's sortBy values.
const SORT_BY_PARAM: Record<SortBy, 'name' | 'role' | 'teams' | 'license'> = {
    name: 'name',
    roles: 'role',
    teams: 'teams',
    licenses: 'license'
};

const ROLE_IDS = ['owner', 'admin', 'billing', 'member'];

function parseMembership(value: string | null): MembershipStatus {
    return value === 'joined' || value === 'invited' ? value : 'all';
}

function parseRoles(value: string | null): Map<string, boolean> {
    const roles = new Map<string, boolean>();
    // Anything unrecognised is dropped rather than trusted — the query string
    // is user-editable.
    value?.split(',').forEach((role) => {
        if (ROLE_IDS.includes(role)) {
            roles.set(role, true);
        }
    });
    return roles;
}

function serialiseRoles(roles: Map<string, boolean>): string {
    return Array.from(roles.entries())
        .filter(([, selected]) => selected)
        .map(([role]) => role)
        .join(',');
}

// Both halves are carried: a selected team may never appear in the loaded
// window, so the URL is the only record of its name. Each half is encoded, so a
// name containing a comma or a colon survives the round trip.
function parseTeams(value: string | null): Map<string, string> {
    const teams = new Map<string, string>();
    value?.split(',').forEach((pair) => {
        const [id, name] = pair.split(':');
        if (id) {
            teams.set(decodeURIComponent(id), decodeURIComponent(name ?? ''));
        }
    });
    return teams;
}

function serialiseTeams(teams: Map<string, string>): string {
    return Array.from(teams.entries())
        .map(
            ([id, name]) =>
                `${encodeURIComponent(id)}:${encodeURIComponent(name)}`
        )
        .join(',');
}

function parseSortBy(value: string | null): SortBy {
    return value && value in SORT_BY_PARAM ? (value as SortBy) : 'name';
}

export function CompanyUsersList(): ReactElement {
    const { t } = useCompaniesTranslation();

    const { company } = useCompanyContext();

    // Company-wide counts feed the "last owner" and "no seats left" guards on
    // the row actions, so the table waits for them rather than rendering
    // actions that don't yet know the limits.
    const {
        counts,
        status: countsStatus,
        refetch: refetchCounts
    } = useFetchUserCounts(company.id);

    // Seeded from the query string, then written back to it below. Read once,
    // on mount: after that this component owns the values, and re-reading would
    // fight the user as they type.
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState(
        () => searchParams.get('q') ?? ''
    );
    const [membershipFilter, setMembershipFilter] = useState<MembershipStatus>(
        () => parseMembership(searchParams.get('membership'))
    );
    const [rolesFilter, setRolesFilter] = useState<Map<string, boolean>>(() =>
        parseRoles(searchParams.get('roles'))
    );
    // Id → name. The name matters: the teams group is paged from the server, so
    // a selected team can fall outside the loaded window, and this is the only
    // record of what it was called.
    const [selectedTeams, setSelectedTeams] = useState<Map<string, string>>(
        () => parseTeams(searchParams.get('teams'))
    );
    const selectedCount = useMemo(() => {
        let count = 0;
        if (membershipFilter !== 'all') {
            count++;
        }

        rolesFilter.forEach((value) => {
            if (value) {
                count++;
            }
        });

        count += selectedTeams.size;

        return count;
    }, [membershipFilter, rolesFilter, selectedTeams]);

    function clearSearch() {
        setSearchQuery('');
        setMembershipFilter('all');
        setRolesFilter(new Map());
        setSelectedTeams(new Map());
    }

    // The teams group is fetched a page at a time, and only once the filter has
    // been opened — the members list itself never needs the team list.
    const {
        search: teamSearch,
        setSearch: setTeamSearch,
        debouncedSearch: debouncedTeamSearch,
        hasOpened: filterHasOpened,
        onOpenChanged: onFilterOpenChanged
    } = useFilterPopupState();

    const teamOptions = useFetchTeamFilterOptions({
        companyId: company.id,
        search: debouncedTeamSearch,
        enabled: filterHasOpened
    });

    // The filter callback identifies a group by its (translated) name, so both
    // sides read from here rather than repeating the string.
    const groupNames = useMemo(
        () => ({
            membership: t('shared.members.filter_membership'),
            roles: t('shared.members.filter_roles'),
            teams: t('components.company_users_list.filter_teams')
        }),
        [t]
    );

    const noTeamLabel = t('components.company_users_list.filter_no_team');

    // Selected teams are pinned above the loaded page so they stay visible (and
    // deselectable) once the search or the window no longer contains them.
    const teamItems = useMemo(() => {
        const query = debouncedTeamSearch.toLowerCase();
        const items: UIMultiFilterGroupItem[] = [];

        if (!query || noTeamLabel.toLowerCase().includes(query)) {
            items.push({
                name: noTeamLabel,
                id: 'no-team',
                selected: selectedTeams.has('no-team')
            });
        }

        selectedTeams.forEach((name, id) => {
            if (id !== 'no-team') {
                items.push({ name, id, selected: true });
            }
        });

        teamOptions.options.forEach((option) => {
            if (!selectedTeams.has(option.id)) {
                items.push({
                    name: option.name,
                    id: option.id,
                    selected: false
                });
            }
        });

        return items;
    }, [teamOptions.options, selectedTeams, debouncedTeamSearch, noTeamLabel]);

    const groups: UIMultiFilterGroup[] = useMemo(() => {
        const items: UIMultiFilterGroup[] = [
            {
                name: groupNames.membership,
                type: 'single',
                items: [
                    {
                        name: t('shared.members.filter_membership_all'),
                        id: 'all',
                        selected: membershipFilter === 'all'
                    },
                    {
                        name: t('shared.members.filter_membership_joined'),
                        id: 'joined',
                        selected: membershipFilter === 'joined'
                    },
                    {
                        name: t('shared.members.filter_membership_invited'),
                        id: 'invited',
                        selected: membershipFilter === 'invited'
                    }
                ]
            },
            {
                name: groupNames.roles,
                type: 'multi',
                items: [
                    {
                        name: t('shared.roles.company.owner.name'),
                        id: 'owner',
                        selected: rolesFilter.get('owner') ?? false
                    },
                    {
                        name: t('shared.roles.company.admin.name'),
                        id: 'admin',
                        selected: rolesFilter.get('admin') ?? false
                    },
                    {
                        name: t('shared.roles.company.billing.name'),
                        id: 'billing',
                        selected: rolesFilter.get('billing') ?? false
                    },
                    {
                        name: t('shared.roles.company.member.name'),
                        id: 'member',
                        selected: rolesFilter.get('member') ?? false
                    }
                ]
            },
            {
                name: groupNames.teams,
                type: 'multi',
                items: teamItems,
                emptyText: t('components.company_users_list.no_teams'),
                paging: {
                    search: teamSearch,
                    onSearchChange: setTeamSearch,
                    searchPlaceholder: t('shared.labels.search'),
                    errorText: t('shared.errors.load_teams'),
                    isLoading:
                        filterHasOpened && teamOptions.status === 'pending',
                    isError: teamOptions.status === 'error',
                    isFetchingNextPage: teamOptions.isFetchingNextPage,
                    hasNextPage: teamOptions.hasNextPage,
                    onLoadMore: teamOptions.fetchNextPage,
                    onRetry: teamOptions.refetch
                }
            }
        ];

        return items;
    }, [
        membershipFilter,
        rolesFilter,
        groupNames,
        teamItems,
        teamSearch,
        setTeamSearch,
        filterHasOpened,
        teamOptions.status,
        teamOptions.isFetchingNextPage,
        teamOptions.hasNextPage,
        teamOptions.fetchNextPage,
        teamOptions.refetch,
        t
    ]);

    const [sortBy, setSortBy] = useState<SortBy>(() =>
        parseSortBy(searchParams.get('sort'))
    );
    const [sortDirection, setSortDirection] = useState<UITableSortDirection>(
        () => parseSortOrder(searchParams.get('order'))
    );

    const debouncedSearch = useDebouncedValue(searchQuery.trim());

    // Mirrored into the query string so opening a member and coming back — or a
    // refresh, or a shared link — lands on the same view. Replaces rather than
    // pushes: a search would otherwise leave a history entry per keystroke. The
    // debounced search is written, not the raw one, so the URL matches the rows
    // actually on screen.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        setOrDeleteParam(next, 'q', debouncedSearch);
        setOrDeleteParam(
            next,
            'membership',
            membershipFilter === 'all' ? '' : membershipFilter
        );
        setOrDeleteParam(next, 'roles', serialiseRoles(rolesFilter));
        setOrDeleteParam(next, 'teams', serialiseTeams(selectedTeams));
        setOrDeleteParam(next, 'sort', sortBy === 'name' ? '' : sortBy);
        setOrDeleteParam(
            next,
            'order',
            sortDirection === 'asc' ? '' : sortDirection
        );

        // Guarded: `searchParams` changes identity on every navigation, and
        // setting it unconditionally would navigate in a loop.
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
    }, [
        searchParams,
        setSearchParams,
        debouncedSearch,
        membershipFilter,
        rolesFilter,
        selectedTeams,
        sortBy,
        sortDirection
    ]);

    const selectedRoles = useMemo(() => {
        return Array.from(rolesFilter.entries())
            .filter(([, selected]) => selected)
            .map(([role]) => role) as (
            | 'owner'
            | 'admin'
            | 'billing'
            | 'member'
        )[];
    }, [rolesFilter]);

    // 'no-team' is a synthetic option, sent as `hasTeam: false`. It used to be
    // an excludeTeamIds list over every team in the company, which needed the
    // whole roster in memory and cannot survive a paginated team list.
    const noTeamSelected = selectedTeams.has('no-team');
    const selectedTeamIds = useMemo(() => {
        return Array.from(selectedTeams.keys()).filter(
            (id) => id !== 'no-team'
        );
    }, [selectedTeams]);

    const {
        users,
        status,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        refetch
    } = useFetchUsersPaginated({
        companyId: company.id,
        search: debouncedSearch,
        teamIds: selectedTeamIds,
        hasTeam: noTeamSelected ? false : undefined,
        membership: membershipFilter,
        roles: selectedRoles,
        sortBy: SORT_BY_PARAM[sortBy],
        sortOrder: sortDirection
    });

    // The rows and the counts that guard their actions are shown together —
    // rendering rows first would briefly offer actions against unknown limits.
    const listStatus =
        status === 'error' || countsStatus === 'error'
            ? 'error'
            : status === 'pending' || countsStatus === 'pending'
              ? 'pending'
              : 'success';

    const { ref: bottomRef, inView } = useInView({ threshold: 0 });
    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const [updateCompanyUserRole, setUpdateCompanyUserRole] =
        useState<CompanyUser | null>(null);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
                <UIFilterGroup>
                    <UIMultiFilterItem
                        icon="sliders"
                        text={t('shared.labels.filter')}
                        groups={groups}
                        selectedCount={selectedCount}
                        // Wider than the default to fit the team search box.
                        popoverWidth={260}
                        onOpenChanged={onFilterOpenChanged}
                        onChanged={(newGroups) => {
                            newGroups.forEach((group) => {
                                if (group.name === groupNames.membership) {
                                    const newSelectedMembershipFilter =
                                        newGroups[0].items.find(
                                            (item) => item.selected
                                        )?.id as MembershipStatus;
                                    setMembershipFilter(
                                        newSelectedMembershipFilter
                                    );
                                } else if (group.name === groupNames.roles) {
                                    const newSelectedRoles = new Map<
                                        string,
                                        boolean
                                    >();
                                    group.items.forEach((item) => {
                                        newSelectedRoles.set(
                                            item.id,
                                            item.selected
                                        );
                                    });
                                    setRolesFilter(newSelectedRoles);
                                } else if (group.name === groupNames.teams) {
                                    const newSelectedTeams = new Map<
                                        string,
                                        string
                                    >();
                                    // Carries forward selections that are not
                                    // in the loaded window: only the rows the
                                    // group actually rendered can have changed.
                                    const rendered = new Set(
                                        group.items.map((item) => item.id)
                                    );
                                    selectedTeams.forEach((name, id) => {
                                        if (!rendered.has(id)) {
                                            newSelectedTeams.set(id, name);
                                        }
                                    });
                                    group.items.forEach((item) => {
                                        if (item.selected) {
                                            newSelectedTeams.set(
                                                item.id,
                                                item.name
                                            );
                                        }
                                    });
                                    // 'No team' is the absence of any team and
                                    // can't be combined with team selections —
                                    // whichever was picked last wins.
                                    if (
                                        newSelectedTeams.has('no-team') &&
                                        newSelectedTeams.size > 1
                                    ) {
                                        if (selectedTeams.has('no-team')) {
                                            newSelectedTeams.delete('no-team');
                                        } else {
                                            const name =
                                                newSelectedTeams.get(
                                                    'no-team'
                                                )!;
                                            newSelectedTeams.clear();
                                            newSelectedTeams.set(
                                                'no-team',
                                                name
                                            );
                                        }
                                    }
                                    setSelectedTeams(newSelectedTeams);
                                }
                            });
                        }}
                    />
                </UIFilterGroup>
                <UIFormInput
                    value={searchQuery}
                    placeholder={t('shared.labels.search')}
                    height={20}
                    onChange={setSearchQuery}
                />
            </div>
            {listStatus === 'pending' && <UILoadingState />}
            {listStatus === 'error' && (
                <UIErrorState
                    title={t('shared.errors.load_members')}
                    onRetry={() => {
                        refetch();
                        refetchCounts();
                    }}
                />
            )}
            {listStatus === 'success' && users.length === 0 && (
                <UIEmptyState
                    description={t('shared.labels.no_results')}
                    buttonText={t('shared.labels.clear_search')}
                    emoji="😔"
                    onClick={clearSearch}
                />
            )}
            {listStatus === 'success' && users.length > 0 && (
                <UISection>
                    <table className={styles.userTable}>
                        <thead>
                            <UserTableHeader
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                onClick={(newSortBy, newSortDirection) => {
                                    setSortBy(newSortBy);
                                    setSortDirection(newSortDirection);
                                }}
                            />
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <UserTableRow
                                    key={user.id}
                                    user={user}
                                    // Counts have loaded by the time this
                                    // branch renders — the fallbacks only
                                    // satisfy the type.
                                    ownerCount={counts?.joinedOwner ?? 0}
                                    assignedLicenses={
                                        counts?.assignedLicense ?? 0
                                    }
                                    onUpdateCompanyUserRole={
                                        setUpdateCompanyUserRole
                                    }
                                />
                            ))}
                            {hasNextPage && (
                                <tr ref={bottomRef}>
                                    <td
                                        colSpan={5}
                                        style={{
                                            textAlign: 'center',
                                            padding: 8
                                        }}
                                    >
                                        {isFetchingNextPage && (
                                            <UISpinner color={charcoal} />
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </UISection>
            )}
            <UpdateCompanyRoleModal
                companyUser={updateCompanyUserRole}
                ownerCount={counts?.joinedOwner ?? 0}
                onClose={() => setUpdateCompanyUserRole(null)}
            />
        </div>
    );
}

function UserTableHeader(props: {
    sortBy: SortBy;
    sortDirection: UITableSortDirection;
    onClick: (sortBy: SortBy, sortDirection: UITableSortDirection) => void;
}): ReactElement {
    const { sortBy, sortDirection, onClick } = props;

    const { t } = useCompaniesTranslation();

    return (
        <tr>
            <UITableHeaderCell
                text={t('shared.labels.name')}
                sortable
                sortDirection={sortBy === 'name' ? sortDirection : undefined}
                onClick={(newSortDirection) => {
                    onClick('name', newSortDirection);
                }}
            />
            <UITableHeaderCell
                text={t('shared.members.column_role')}
                tooltip={t('shared.members.column_role_tooltip')}
                sortable
                sortDirection={sortBy === 'roles' ? sortDirection : undefined}
                onClick={(newSortDirection) => {
                    onClick('roles', newSortDirection);
                }}
            />
            <UITableHeaderCell
                text={t('components.company_users_list.column_teams')}
                tooltip={t(
                    'components.company_users_list.column_teams_tooltip'
                )}
                sortable
                sortDirection={sortBy === 'teams' ? sortDirection : undefined}
                onClick={(newSortDirection) => {
                    onClick('teams', newSortDirection);
                }}
            />
            <UITableHeaderCell
                text={t('shared.members.column_license')}
                tooltip={t('shared.members.column_license_tooltip')}
                sortable
                sortDirection={
                    sortBy === 'licenses' ? sortDirection : undefined
                }
                onClick={(newSortDirection) => {
                    onClick('licenses', newSortDirection);
                }}
            />
            <th />
        </tr>
    );
}

function UserTableRow(props: {
    user: CompanyUser;
    ownerCount: number;
    assignedLicenses: number;
    onUpdateCompanyUserRole: (user: CompanyUser) => void;
}): ReactElement {
    const { user, ownerCount, assignedLicenses, onUpdateCompanyUserRole } =
        props;

    const { t } = useCompaniesTranslation();

    const popoverRef = useRef<UIPropoverRef>(null);

    const deleteUserAction = useDeleteCompanyUserAction({ user, ownerCount });
    const assignLicenseAction = useAssignLicenseAction({
        user,
        assignedLicenses
    });
    const removeLicenseAction = useRemoveLicenseAction({
        user
    });
    const resendInviteAction = useResendInviteAction({
        user
    });
    const updateCompanyRoleAction = useUpdateCompanyRoleAction({
        user,
        ownerCount
    });

    const confirmationModal = useUIConfirmationModal();

    const navigate = useNavigate();

    return (
        <tr
            className={styles.tableRow}
            onClick={() => {
                void navigate(
                    `/company/${user.companyId}/settings/members/${user.id}`
                );
            }}
        >
            <td>
                <UserInfoListItem user={user} />
            </td>
            <td>
                <RolesCell user={user} />
            </td>
            <td onClick={(e) => e.stopPropagation()}>
                {user.teams.length === 0 ? (
                    <NoTeamCell onClick={() => {}} />
                ) : (
                    <TeamCell teams={user.teams} companyId={user.companyId} />
                )}
            </td>
            <td>
                <LicenseCell user={user} />
            </td>
            <td onClick={(e) => e.stopPropagation()}>
                <UIPopover
                    ref={popoverRef}
                    placement="auto"
                    popoverPadding={4}
                    trigger="click"
                    triggerNode={
                        <button className={styles.removeButtonStyle}>
                            <UIPressableAreaButtonLess width={16} height={16}>
                                <UIIcon
                                    name={'more-vertical'}
                                    size={16}
                                    color={charcoal}
                                />
                            </UIPressableAreaButtonLess>
                        </button>
                    }
                >
                    <UIMenu>
                        <UIMenuItem
                            text={t('shared.members.action_update_role')}
                            leftIconName="edit-2"
                            disabled={!updateCompanyRoleAction.isAllowed}
                            disabledTooltip={updateCompanyRoleAction.reason}
                            onClick={(e) => {
                                e.stopPropagation();
                                onUpdateCompanyUserRole(user);
                                popoverRef.current?.close();
                            }}
                        />

                        {!user.joined && (
                            <UIMenuItem
                                text={t('shared.members.action_resend_invite')}
                                leftIconName="send"
                                loading={resendInviteAction.loading}
                                disabled={!resendInviteAction.isAllowed}
                                disabledTooltip={resendInviteAction.reason}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    resendInviteAction
                                        .resendInvite()
                                        .catch(() => {}) // Error handled in hook
                                        .finally(() => {
                                            popoverRef.current?.close();
                                        });
                                }}
                            />
                        )}

                        {user.license ? (
                            <UIMenuItem
                                text={t('shared.members.action_remove_license')}
                                leftIconName="x"
                                textColor={red}
                                loading={removeLicenseAction.loading}
                                disabled={!removeLicenseAction.isAllowed}
                                disabledTooltip={removeLicenseAction.reason}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    popoverRef.current?.close();
                                    confirmationModal.open({
                                        title: t(
                                            'shared.members.remove_license_confirm_title'
                                        ),
                                        confirmText: t(
                                            'shared.members.remove_license_confirm_button'
                                        ),
                                        confirmVariant: 'danger',
                                        cancelText: t('shared.labels.cancel'),
                                        onConfirm: () => {
                                            confirmationModal.setLoading(true);
                                            removeLicenseAction
                                                .removeLicense()
                                                .catch(() => {}) // Error handled in hook
                                                .finally(() => {
                                                    confirmationModal.setLoading(
                                                        false
                                                    );
                                                    confirmationModal.close();
                                                });
                                        },
                                        onCancel: () => {
                                            confirmationModal.close();
                                        }
                                    });
                                }}
                            />
                        ) : (
                            <UIMenuItem
                                text={t('shared.members.action_assign_license')}
                                leftIconName="check"
                                loading={assignLicenseAction.loading}
                                disabled={!assignLicenseAction.isAllowed}
                                disabledTooltip={assignLicenseAction.reason}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    assignLicenseAction
                                        .assignLicense()
                                        .catch(() => {}) // Error handled in hook
                                        .finally(() => {
                                            popoverRef.current?.close();
                                        });
                                }}
                            />
                        )}

                        <UIMenuItem
                            text={t('shared.labels.remove')}
                            leftIconName="trash-2"
                            textColor={red}
                            loading={deleteUserAction.loading}
                            disabled={!deleteUserAction.isAllowed}
                            disabledTooltip={deleteUserAction.reason}
                            onClick={(e) => {
                                e.stopPropagation();
                                popoverRef.current?.close();
                                confirmationModal.open({
                                    title: t(
                                        'components.company_users_list.remove_member_confirm_title'
                                    ),
                                    confirmText: t(
                                        'shared.members.remove_member_confirm_button'
                                    ),
                                    confirmVariant: 'danger',
                                    cancelText: t('shared.labels.cancel'),
                                    onConfirm: () => {
                                        confirmationModal.setLoading(true);
                                        deleteUserAction
                                            .deleteUser()
                                            .catch(() => {}) // Error handled in hook
                                            .finally(() => {
                                                confirmationModal.close();
                                            });
                                    },
                                    onCancel: () => {
                                        confirmationModal.close();
                                    }
                                });
                            }}
                        />
                    </UIMenu>
                </UIPopover>
            </td>
        </tr>
    );
}

/**
 * Cells
 */

function RolesCell(props: { user: CompanyUser }): ReactElement {
    const { user } = props;

    const { t } = useCompaniesTranslation();

    return (
        <UIText type={'default2'}>
            {t(`shared.roles.company.${user.role}.name`)}
        </UIText>
    );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function NoTeamCell(_props: { onClick: () => void }): ReactElement {
    //const { onClick } = props;

    const { t } = useCompaniesTranslation();

    return (
        <UITooltip
            text={t('components.company_users_list.no_teams_tooltip')}
            place="top"
        >
            <UIText type={'default2'}>
                {t('components.company_users_list.no_teams')}
            </UIText>
        </UITooltip>
    );
    // return (
    //     <UITextButton
    //         variant="danger"
    //         leftIcon="alert-triangle"
    //         onClick={onClick}
    //     >
    //         No teams
    //     </UITextButton>
    // );
}

function TeamCell(props: {
    teams: TeamUser[];
    companyId: string;
}): ReactElement {
    const { teams, companyId } = props;

    const { t } = useCompaniesTranslation();

    const [divRef, size] = useElementSize<HTMLDivElement>();

    const popoverContent = useCallback(
        (divRef?: RefObject<HTMLDivElement | null>) => (
            <div ref={divRef}>
                <UIMenu
                    maxHeight={400}
                    scroll
                    // header={
                    //     <div
                    //         style={{
                    //             paddingLeft: 8,
                    //             paddingRight: 8
                    //         }}
                    //     >
                    //         <UIText variant="h3">{`${teams.length} ${teams.length === 1 ? 'Team' : 'Teams'}`}</UIText>
                    //     </div>
                    // }
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8
                        }}
                    >
                        {teams.map((team) => (
                            <TeamPopoverRow
                                key={team.teamId}
                                team={team}
                                companyId={companyId}
                            />
                        ))}
                    </div>
                </UIMenu>
            </div>
        ),
        [teams, companyId]
    );

    return (
        <>
            <UIPopover
                placement="auto"
                trigger="hover"
                width={size.width}
                triggerNode={
                    <div>
                        <UIText type="link">
                            {t('components.company_users_list.team_count', {
                                count: teams.length
                            })}
                        </UIText>
                    </div>
                }
            >
                {popoverContent()}
            </UIPopover>
            {/* Dummy to get width of popover content */}
            <div
                style={{
                    position: 'absolute',
                    opacity: 0,
                    pointerEvents: 'none'
                }}
            >
                {popoverContent(divRef)}
            </div>
        </>
    );
}

function TeamPopoverRow(props: {
    team: TeamUser;
    companyId: string;
}): ReactElement {
    const { team, companyId } = props;

    // A member can belong to teams the viewer isn't in. Those are still listed
    // — the membership is the point — but they cannot be opened.
    const canView = team.capabilities.canView;
    const teamLink = `/company/${companyId}/settings/teams/${team.teamId}`;
    // The whole row is the hover target for the "why not" tooltip, so it is
    // anchored by id rather than wrapping anything.
    const rowId = useMemo(() => `team-popover-row-${uuidv4()}`, []);

    const navigate = useNavigate();

    return (
        <div
            id={canView.allowed ? undefined : rowId}
            className={canView.allowed ? styles.teamRow : styles.teamRowLocked}
            onClick={
                canView.allowed
                    ? () => {
                          void navigate(teamLink);
                      }
                    : undefined
            }
        >
            <UIAvatar size={40} img={team.image ?? ''} alt={`${team.name}`} />
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 4
                }}
            >
                {canView.allowed ? (
                    <Link to={teamLink} style={{ textDecoration: 'none' }}>
                        <UIText type="link" medium>
                            {team.name}
                        </UIText>
                    </Link>
                ) : (
                    <>
                        <UIText medium>{team.name}</UIText>
                        <UITooltip
                            anchorId={rowId}
                            text={canView.reason ?? ''}
                            place="top"
                        />
                    </>
                )}
                <TeamUserRoles roleData={team.roles} small />
            </div>
        </div>
    );
}

function LicenseCell(props: { user: CompanyUser }): ReactElement {
    const { user } = props;
    // return <UIText type={'default2'}>{user.license ? 'Yes' : 'No'}</UIText>;

    const { t } = useCompaniesTranslation();

    const hasLicense = !!user.license;
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}
        >
            <UITooltip
                text={
                    hasLicense
                        ? t('shared.members.license_assigned')
                        : t('shared.members.license_not_assigned')
                }
                place="top"
            >
                <UIIcon
                    name={hasLicense ? 'check' : 'x'}
                    color={hasLicense ? blue : red}
                />
            </UITooltip>
        </div>
    );
}
